import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import api, * as API from "../../utils/api";
import * as localStorage from "../../utils/localStorage";
import jwtDecode from "jwt-decode";
import { IMG_BASE_URL } from "../../constants";
import { applicationStatusSlice } from "../applicationStatusSlice";
import {
  MyUser,
  Roles,
  LoginAPIResponse,
  UserAPIResponse,
  RoleAPIResponse,
} from "./userInterfaces";
import { mobileCheck } from "../../utils/helpers";
import { mixpanel } from "../../utils/mixpanel";
import {
  getOAuthClient,
  createHandleGatekeeperOAuthCallback,
  storeGatekeeperOAuthState,
} from "../../utils/oauth";

// Flag to prevent multiple concurrent magic link requests
let magicLinkRequestInProgress = false;

type MyUserState = MyUser | null;

const initialState: MyUserState = null;

function jwtToUser(jwt: string): UserAPIResponse {
  const {
    user: {
      id,
      attributes: { firstName, lastName, email, phone, imageId, imageVersion },
    },
  }: {
    user: {
      id: number;
      type: "weserve";
      attributes: {
        firstName: string;
        lastName: string;
        email: string;
        phone?: string;
        imageId: string;
        imageVersion: string;
      };
    };
  } = jwtDecode(jwt);
  return {
    id,
    firstName,
    lastName,
    email,
    phone: phone || "",
    imageId,
    imageVersion,
  };
}

export const setUserInfo = createAsyncThunk<MyUser, LoginAPIResponse>(
  "myUser/setUserInfo",
  async (data, thunkApi) => {
    // set auth token for all future requests
    API.setTokens(data.token);
    localStorage.setTokens(data.token);

    const user = jwtToUser(data.token.auth);
    (window as any).Appcues.identify(user.id, { name: user.firstName });
    mixpanel.identify(user.id);

    const { organization }: RootState = thunkApi.getState() as any;
    const {
      data: { records: userRoles },
    } = await api.get<RoleAPIResponse>(
      `/organizations/${organization.id}/financial-roles`
    );
    const userRoleNames = userRoles?.map((r) => r.name) || [];
    const roles: Roles = {
      portalAdmin: userRoleNames.includes("portalAdmin"),
      analyst: userRoleNames.includes("analyst"),
      portalUser: userRoleNames.includes("portalUser"),
    };

    const isAdminPortalUser =
      roles.portalAdmin || roles.analyst || roles.portalUser;

    const isMobile = mobileCheck();
    const invalidMobileUser = isMobile && isAdminPortalUser;

    thunkApi.dispatch(applicationStatusSlice.actions.setAuthorization(true));

    return {
      invalidMobileUser,
      roles: roles,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      imgUrl:
        user.imageId === "avatar"
          ? null
          : `${IMG_BASE_URL}/${user.imageVersion}/${user.imageId}`,
      userId: user.id,
      isAdminPortalUser,
    };
  }
);

export const login = createAsyncThunk<
  MyUser,
  { email: string; password: string }
>("myUser/login", async ({ email, password }, thunkApi) => {
  try {
    const { data } = await api.post<LoginAPIResponse>("/auth/login", {
      email,
      password: btoa(password),
    });
    const response = await thunkApi.dispatch(setUserInfo(data));
    return response.payload as MyUser;
  } catch (e) {
    return Promise.reject(e);
  }
});

export const logout = createAsyncThunk<void>(
  "myUser/logout",
  async (_, thunkApi) => {
    // Get tokens for logout - use localStorage to survive re-initialization
    const tokens = localStorage.getTokens();
    const refreshToken = tokens?.refresh;
    const authToken = tokens?.auth;

    try {
      // Call logout endpoint to trigger shared logout via gatekeeper
      // This endpoint does NOT require authentication - it accepts expired tokens
      await api.post(
        "/auth/logout",
        { authToken }, // Pass authToken in body to extract OAuth token ID
        {
          headers: { "X-Cotribute-Refresh-Token": refreshToken },
        }
      );
    } catch (error) {
      console.error("Logout API call failed:", error);
      // Continue with local logout even if API call fails
      // Backend logout endpoint should never fail (always returns 204)
    }

    localStorage.clearTokens();
    API.clearTokens();

    thunkApi.dispatch(applicationStatusSlice.actions.setAuthorization(false));
    thunkApi.dispatch(myUserSlice.actions.clearUser());
  }
);

export const loginByRefreshToken = createAsyncThunk<
  void,
  { refreshToken: string; isFromMagicLink?: boolean }
>(
  "myUser/loginByRefreshToken",
  async ({ refreshToken, isFromMagicLink }, thunkApi) => {
    try {
      // Get the current JWT from localStorage to preserve OAuth session info during refresh
      // If the JWT is still valid, backend will return it; if expired, backend generates a new one
      const tokens = localStorage.getTokens();
      const currentAuthToken = tokens?.auth;

      const { data } = await api.post(
        "/auth/tokens/verify",
        { authToken: currentAuthToken }, // Pass current JWT to preserve oAuthToken.id
        { headers: { "X-Cotribute-Refresh-Token": refreshToken } }
      );
      await thunkApi.dispatch(setUserInfo(data));
    } catch (e: any) {
      if (e?.response?.status === 401) {
        // Only send magic link if this is from a magic link (hash parameter)
        // For all other scenarios (app init, interceptor), go to login page instead
        if (isFromMagicLink) {
          // Only send magic link if not already in progress
          if (!magicLinkRequestInProgress) {
            magicLinkRequestInProgress = true;
            try {
              await api.post(
                "auth/tokens/send",
                {
                  path: window.location.pathname,
                },
                { headers: { "X-Cotribute-Refresh-Token": refreshToken } }
              );
            } catch (e) {
              console.error("There was an error generating a magic link", e);
            } finally {
              // Reset flag after a delay to allow for subsequent attempts if needed
              setTimeout(() => {
                magicLinkRequestInProgress = false;
              }, 30000); // 30 second cooldown
            }
          }

          // Clear tokens BEFORE redirecting to prevent re-initialization loop
          localStorage.clearTokens();
          API.clearTokens();

          // Log out the user (but don't dispatch logout to avoid additional side effects)
          thunkApi.dispatch(
            applicationStatusSlice.actions.setAuthorization(false)
          );
          thunkApi.dispatch(myUserSlice.actions.clearUser());

          // Redirect to login-error page to show "check your email" message
          let org = "";
          const matches =
            window.location.pathname.match(/organization\/(.*)\//);
          if (matches) org = matches[1];
          const redirectUrl = `/organization/${org.split("/")[0]}/login-error`;
          window.location.replace(redirectUrl);
        }
      }

      // For non-magic-link scenarios, just reject - caller will handle redirect to login
      return Promise.reject("Unauthorized");
    }
  }
);

export const loginViaOAuth = createAsyncThunk<MyUser>(
  "myUser/loginViaOAuth",
  async (_, thunkApi) => {
    try {
      const state: RootState = thunkApi.getState() as RootState;
      const organizationId = state.organization.id;

      if (!organizationId) {
        throw new Error("Organization ID not available");
      }

      const tokens = await getOAuthClient().handleCallback(
        createHandleGatekeeperOAuthCallback(organizationId)
      );
      if (!tokens) {
        throw new Error("No tokens received from OAuth callback");
      }

      const response = await thunkApi.dispatch(setUserInfo({ token: tokens }));
      return response.payload as MyUser;
    } catch (error) {
      console.error("OAuth login failed:", error);
      throw error;
    }
  }
);

export const initiateOAuthLogin = createAsyncThunk<void>(
  "myUser/initiateOAuthLogin",
  async () => {
    const { pathname } = window.location;
    const orgSlug = pathname.split("/")[2]; // Extract from /organization/{slug}/...

    await getOAuthClient().redirectToAuthWithEnv({
      gatekeeperBaseUrl: process.env.REACT_APP_GATEKEEPER_BASE_URL!,
      organizationSlug: orgSlug,
      storeOAuthState: storeGatekeeperOAuthState,
    });
  }
);

export const myUserSlice = createSlice<any, { clearUser: () => null }>({
  name: "myUser",
  initialState: initialState as MyUserState,
  reducers: {
    clearUser: () => {
      return null;
    },
  },
  extraReducers: {
    [setUserInfo.fulfilled.type]: (state, { payload }) => {
      return payload;
    },
    [logout.fulfilled.type]: (state) => {
      return null;
    },
  },
});

export const selectMyUser = (state: RootState) =>
  state.myUser as MyUser | undefined;

export default myUserSlice.reducer;
